import React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { ExpandMore, HighlightOff } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';

import { FormattedMessage } from 'react-intl';
import { mapRecordLogToFullAuditLogTable } from '../utils';
import { DataGridPro, GRID_DETAIL_PANEL_TOGGLE_FIELD, GridValidRowModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { historyReportColumns } from './detailDialogColumns';
import { LogReportMode } from '../PropertiesPanel/utils';
import { attachmentLogsColumns } from '../Attachments/columns';

export type FullHistoryDialogProps = {
    open: boolean;
    onClose: () => void;
    items?: any[];
    onFetchMore: () => void;
    total: number;
    logReportMode: LogReportMode;
};

const useStyles = makeStyles((theme) => ({
    evenRow: {},
    oddRow: {
        backgroundColor: `${theme.palette.primary[200]} !important`
    }
}));

const CustomDetailPanelToggle = (props: { shouldShow: boolean }) => {
    const { shouldShow } = props;
    return (
        <>
            {shouldShow ? (
                <IconButton tabIndex={-1}>
                    <ExpandMore
                        sx={{
                            transition: (theme) =>
                                theme.transitions.create('transform', {
                                    duration: theme.transitions.duration.shortest
                                })
                        }}
                        fontSize="inherit"
                    />
                </IconButton>
            ) : (
                <Box />
            )}
        </>
    );
};

const HistoryPanelContent = ({ row }: { row: GridValidRowModel }) => (
    <>
        {row.objectLogs ? (
            <DataGridPro
                columns={[
                    {
                        field: 'id',
                        width: 40,
                        renderHeader: () => '',
                        renderCell: () => ''
                    },
                    ...historyReportColumns
                ]}
                autoHeight
                rows={row.objectLogs}
                getRowHeight={() => 'auto'}
                onCellKeyDown={(_params, event) => {
                    event.defaultMuiPrevented = true;
                }}
                sx={(theme) => ({
                    '& *::-webkit-scrollbar-thumb': {
                        borderRadius: '8px',
                        background: '#C0C0C0',
                        width: '4px'
                    },
                    '& *::-webkit-scrollbar': {
                        width: '4px',
                        borderRadius: '8px',
                        height: '8px'
                    },
                    '& .MuiDataGrid-columnHeaders': { display: 'none' },
                    '& .MuiDataGrid-virtualScroller': {
                        marginTop: '0 !important',
                        border: `solid 1px ${theme.palette.secondary.main}`,
                        overflowX: 'hidden'
                    },
                    '& .MuiDataGrid-row': {
                        alignItems: 'center',
                        border: 'solid 1px #dddddd',
                        borderRight: 'none',
                        borderLeft: 'none',
                        py: 1
                    },
                    '&.MuiDataGrid-root .MuiDataGrid-cell': {
                        outlineColor: 'transparent !important',
                        outlineWidth: 0
                    },
                    '&.MuiDataGrid-root .MuiDataGrid-cell:focus': {
                        outlineColor: 'transparent !important',
                        outlineWidth: 0
                    }
                })}
                disableRowSelectionOnClick
                disableColumnMenu
                disableColumnFilter
                disableColumnSelector
                hideFooter
            />
        ) : (
            <></>
        )}
    </>
);

export const FullHistoryDialog = ({ open, onClose, items, onFetchMore, total, logReportMode }: FullHistoryDialogProps) => {
    const classes = useStyles();
    const rows = (
        items?.map((item) => mapRecordLogToFullAuditLogTable(item, logReportMode)).reduce((pre, cur) => pre.concat(cur), []) ?? []
    ).filter((item) => item.fieldName.length > 0 || item.activity.toLowerCase() !== 'update');

    const apiRef = useGridApiRef();

    const handleOnCloseFullHistory = () => {
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleOnCloseFullHistory} maxWidth="xl">
            {open && (
                <>
                    <DialogTitle
                        id="form-dialog-title"
                        sx={{
                            fontSize: '24px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            paddingBottom: 0
                        }}
                    >
                        <Box>
                            <FormattedMessage id="Full History" />
                        </Box>
                        <IconButton onClick={handleOnCloseFullHistory}>
                            <HighlightOff sx={(theme) => ({ color: theme.palette.secondary.main })} />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ paddingBottom: 0 }}>
                        <Box sx={{ height: '400px' }}>
                            <DataGridPro
                                apiRef={apiRef}
                                columns={[
                                    {
                                        field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
                                        width: 40,
                                        renderHeader: () => '',
                                        renderCell: (params) => <CustomDetailPanelToggle shouldShow={params.row.objectLogs.length > 0} />
                                    },
                                    ...(logReportMode === LogReportMode.Attachments ? attachmentLogsColumns : historyReportColumns)
                                ]}
                                rows={rows}
                                getRowHeight={() => 'auto'}
                                getRowClassName={(params) => {
                                    if (apiRef.current.state.detailPanel.expandedRowIds.includes(params.id))
                                        return 'row-detail-panel-expanded';
                                    return params.indexRelativeToCurrentPage % 2 !== 0 ? classes.oddRow : classes.evenRow;
                                }}
                                getDetailPanelHeight={() => 'auto'}
                                getDetailPanelContent={HistoryPanelContent}
                                onCellKeyDown={(_params, event) => {
                                    event.defaultMuiPrevented = true;
                                }}
                                sx={(theme) => ({
                                    '& .MuiDataGrid-row': {
                                        alignItems: 'center',
                                        border: 'solid 1px #dddddd',
                                        borderRight: 'none',
                                        borderLeft: 'none',
                                        py: 1
                                    },
                                    '& [data-field="__detail_panel_toggle__"]': {
                                        userSelect: 'none',
                                        outline: '0 !important'
                                    },
                                    '&.MuiDataGrid-root .MuiDataGrid-cell:focus': { outline: 'none' },
                                    '& div.MuiDataGrid-withBorderColor': {
                                        border: 'none !important'
                                    },
                                    '& div.row-detail-panel-expanded': {
                                        backgroundColor: `${theme.palette.secondary.main} !important`,
                                        color: '#fff',
                                        '& svg[data-testid="ExpandMoreIcon"]': {
                                            color: '#fff',
                                            transform: `rotateZ(180deg)`
                                        },
                                        '& div.MuiChip-root': {
                                            borderColor: '#fff'
                                        },
                                        '& p': {
                                            color: '#fff !important'
                                        }
                                    }
                                })}
                                disableRowSelectionOnClick
                                disableColumnMenu
                                disableColumnFilter
                                disableColumnSelector
                                hideFooter
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ pr: 2.5 }}>
                        <Button variant="contained" color="secondary" onClick={onClose} size="small">
                            Done
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export default FullHistoryDialog;
